/**
 * StudentCertificateEdit.tsx
 *
 * Student-only edit form for course certificates.
 * SECURITY CONTRACT:
 *   - Renders only the 6 student-editable fields (whitelist enforced on UI layer).
 *   - The form payload sent to the backend contains ONLY these fields + the
 *     certificate id. All other fields (status, student_id, logs, etc.) are
 *     NOT included in the submission — the backend re-applies them from the DB.
 *   - File upload: validates type (PDF/image) and size (≤5 MB) before submission.
 *   - On backend 403: shows a clear error; does NOT redirect silently.
 *
 * Admin restrictions: This component is NEVER shown to admins.
 * The route /student/edit/certificate/:id is protected by requiredRoles={["student"]}.
 */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiConfig from "../../config/apiConfig";
import { getCookie, fetchEnum } from "../../apis/enum";
import { logout } from "../../apis/backend";
import { LoginContext } from "../../context/LoginContext";
import Sidebar from "../Utils/Sidebar";
import styles from "../Styles/CreateCertificate.module.css";
import { LogOut } from "lucide-react";

// ─── Auth-aware fetch helper ───────────────────────────────────────────────────
async function authFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const finalInit: RequestInit = {
    credentials: "include",
    ...init,
    headers: { ...(init.headers || {}) },
  };
  const token = getCookie("access_token");
  const hdrs = finalInit.headers as any;
  if (token && !hdrs["Authorization"]) {
    hdrs["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(input, finalInit);
  if (res.status === 401) {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
    throw new Error("Unauthorized");
  }
  return res;
}

// ─── Allowed MIME types for certificate upload ─────────────────────────────────
const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

// ─── Status badge (read-only display) ─────────────────────────────────────────
const StatusBadge = ({ status }: { status: any }) => {
  const s = String(status).toLowerCase();
  if (s === "true")
    return (
      <span className="badge bg-success text-white" style={{ fontSize: 14, padding: "6px 14px", borderRadius: 8 }}>
        ✅ Approved
      </span>
    );
  if (s === "false")
    return (
      <span className="badge bg-danger text-white" style={{ fontSize: 14, padding: "6px 14px", borderRadius: 8 }}>
        ❌ Rejected
      </span>
    );
  return (
    <span className="badge bg-warning text-dark" style={{ fontSize: 14, padding: "6px 14px", borderRadius: 8 }}>
      ⏳ Pending
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const StudentCertificateEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useContext(LoginContext);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Form state — only the 6 editable fields + read-only status for display
  const [courseName, setCourseName] = useState("");
  const [courseDuration, setCourseDuration] = useState("");
  const [courseMode, setCourseMode] = useState("");
  const [platform, setPlatform] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [existingStatus, setExistingStatus] = useState<any>(null); // read-only display
  const [existingDocId, setExistingDocId] = useState<string | null>(null);
  const [existingStudentId, setExistingStudentId] = useState<string | null>(null);

  // UI state
  const [courseModes, setCourseModes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  // ── Fetch course modes enum ────────────────────────────────────────────────
  useEffect(() => {
    fetchEnum("Course_mode")
      .then((data: any) => {
        const modes: string[] = Array.isArray(data?.resource)
          ? data.resource.map((r: any) => r.name || r)
          : Array.isArray(data)
          ? data
          : [];
        setCourseModes(modes);
      })
      .catch(console.error);
  }, []);

  // ── Load existing certificate (pre-fill form) ──────────────────────────────
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ queryId: "GET_BY_ID", args: `id:${id}` });
        const res = await authFetch(
          `${apiConfig.getResourceUrl("certificate")}?${params.toString()}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        if (!res.ok) throw new Error("Failed to load certificate (HTTP " + res.status + ")");
        const json = await res.json();
        const cert = json?.resource?.[0];
        if (!cert) throw new Error("Certificate not found.");

        // Pre-fill editable fields
        setCourseName(cert.course_name || "");
        setCourseDuration(cert.course_duration != null ? String(cert.course_duration) : "");
        setCourseMode(cert.course_mode || "");
        setPlatform(cert.platform || "");

        // Normalize date to YYYY-MM-DD for <input type="date">
        if (cert.course_completion_date) {
          const d = new Date(cert.course_completion_date);
          const iso = isNaN(d.getTime())
            ? cert.course_completion_date
            : d.toISOString().split("T")[0];
          setCompletionDate(iso);
        }

        // Read-only / system fields
        setExistingStatus(cert.status);
        setExistingDocId(cert.upload_certificate || null);
        setExistingStudentId(cert.student_id || null);
      } catch (e: any) {
        setError(e.message || "Failed to load certificate.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ── File validation ────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setFileError("Only PDF, JPG, or PNG files are allowed.");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("File size must not exceed 5 MB.");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  // ── Form submission ────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setError(null);

    // Client-side validation
    if (!courseName.trim()) return setError("Course Name is required.");
    const durationNum = parseInt(courseDuration, 10);
    if (!courseDuration || isNaN(durationNum) || durationNum < 0) {
      return setError("Course Duration must be a non-negative number.");
    }
    if (!courseMode.trim()) return setError("Course Mode is required.");
    if (!platform.trim())   return setError("Platform is required.");
    if (!completionDate)    return setError("Completion Date is required.");

    setSubmitting(true);
    try {
      const formData = new FormData();

      // ── Build WHITELIST-ONLY payload ──────────────────────────────────────
      // IMPORTANT: Only these 6 fields + id are included.
      // status, student_id, logs, course_url, and all g_* fields are
      // intentionally OMITTED — they will be restored from DB by the backend.
      const payload: Record<string, any> = {
        id,
        student_id: existingStudentId || "",
        course_name: courseName.trim(),
        course_duration: durationNum,
        course_mode: courseMode.trim(),
        platform: platform.trim(),
        course_completion_date: completionDate,
        upload_certificate: selectedFile ? "" : (existingDocId || "existing"),
      };

      // Upload new certificate file (optional — only if student chose one)
      if (selectedFile) {
        formData.append("file", selectedFile);
        formData.append("description", "Student certificate edit");
        formData.append("appId", "VALP ID");
        formData.append("dmsRole", "admin");
        formData.append("user_id", user?.email_id || "student");
        formData.append("tags", "certificate,student");
      }

      const base64Payload = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      formData.append("resource", base64Payload);
      formData.append("action", "MODIFY");

      const res = await authFetch(`${apiConfig.getResourceUrl("certificate")}?`, {
        method: "POST",
        body: formData,
      });

      const text = await res.text();

      if (!res.ok) {
        // Parse backend error
        let msg = "Update failed (HTTP " + res.status + ").";
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) msg = parsed.message;
        } catch (_) {}
        // Surface 403 errors clearly
        if (res.status === 403 || (msg && msg.includes("403 Forbidden"))) {
          msg = "⛔ " + msg;
        }
        setError(msg);
        return;
      }

      // Success
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigate("/records");
      }, 1800);
    } catch (e: any) {
      setError(e.message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    const ok = await logout();
    if (ok) navigate("/");
  };

  const isLocked = existingStatus === true || existingStatus === "true";
  const isRejected = existingStatus === false || existingStatus === "false";

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          fontSize: 18,
          color: "#555",
        }}
      >
        Loading certificate…
      </div>
    );
  }

  return (
    <div className="page12Container">
      {/* Sidebar */}
      <Sidebar
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p) => !p)}
        activeSection="dashboard"
      />

      <main className={`mainContent ${sidebarCollapsed ? "sidebarCollapsed" : ""}`}>
        {/* Header */}
        <header className="contentHeader">
          <h1 className="pageTitle">Edit Certificate</h1>

          <div className="userProfile" style={{ position: "relative" }}>
            <div
              className="profileCircle"
              onClick={() => setShowDropdown((p) => !p)}
              style={{ cursor: "pointer" }}
            >
              <span className="profileInitial">
                {user?.email_id?.charAt(0)?.toUpperCase() || "S"}
              </span>
            </div>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "45px",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                  padding: "10px",
                  minWidth: "130px",
                  zIndex: 100,
                }}
              >
                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    padding: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <LogOut size={16} />
                  <span style={{ marginLeft: "8px" }}>Logout</span>
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="contentBody">
          <div className="pageFormContainer">
            {/* Global error */}
            {error && (
              <div
                className="alert alert-danger"
                style={{ borderRadius: 10, marginBottom: 20, fontSize: 15 }}
              >
                {error}
              </div>
            )}

            {/* Lock warning if Approved */}
            {isLocked && (
              <div
                className="alert alert-warning d-flex align-items-center gap-2"
                style={{ borderRadius: 10, marginBottom: 20, fontSize: 15 }}
              >
                <span>🔒</span>
                <span>
                  This certificate has been <strong>Approved</strong> and cannot be edited by students.
                </span>
              </div>
            )}

            {/* Info alert if Rejected */}
            {isRejected && (
              <div
                className="alert alert-info d-flex align-items-center gap-2"
                style={{ borderRadius: 10, marginBottom: 20, fontSize: 15 }}
              >
                <span>ℹ️</span>
                <span>
                  This certificate was <strong>Rejected</strong>. You can correct your details or upload a new certificate to resubmit for review.
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.certificateFormWrapper}>
                <h2 className={styles.sectionTitle}>Edit Your Certificate</h2>

                {/* ── Read-only status banner ────────────────────────────────── */}
                <div
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: 10,
                    padding: "12px 18px",
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#555" }}>Approval Status:</span>
                  <StatusBadge status={existingStatus} />
                  <span style={{ fontSize: 13, color: "#888", marginLeft: "auto" }}>
                    Status can only be changed by an admin.
                  </span>
                </div>

                {/* ── Form grid (6 editable fields only) ───────────────────────── */}
                <div className={styles.formGrid}>

                  {/* 1. Course Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-course-name">
                      <span className={styles.required}>*</span> Course Name
                    </label>
                    <input
                      id="sce-course-name"
                      type="text"
                      className={styles.formControl}
                      required
                      maxLength={200}
                      value={courseName}
                      disabled={isLocked}
                      onChange={(e) => setCourseName(e.target.value)}
                      placeholder="e.g. Machine Learning Fundamentals"
                    />
                  </div>

                  {/* 2. Course Duration */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-course-duration">
                      <span className={styles.required}>*</span> Course Duration (in hours)
                    </label>
                    <input
                      id="sce-course-duration"
                      type="number"
                      min={0}
                      step={1}
                      className={styles.formControl}
                      required
                      value={courseDuration}
                      disabled={isLocked}
                      onChange={(e) => setCourseDuration(e.target.value)}
                      placeholder="e.g. 40"
                    />
                  </div>

                  {/* 3. Course Mode */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-course-mode">
                      <span className={styles.required}>*</span> Course Mode
                    </label>
                    <select
                      id="sce-course-mode"
                      className={styles.formControl}
                      required
                      value={courseMode}
                      disabled={isLocked}
                      onChange={(e) => setCourseMode(e.target.value)}
                    >
                      <option value="">Select course mode</option>
                      {courseModes.length > 0
                        ? courseModes.map((m, i) => (
                            <option key={i} value={m}>
                              {m}
                            </option>
                          ))
                        : /* Fallback hardcoded values in case enum fetch fails */
                          ["Online", "Offline", "Hybrid"].map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                    </select>
                  </div>

                  {/* 4. Platform */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-platform">
                      <span className={styles.required}>*</span> Platform
                    </label>
                    <input
                      id="sce-platform"
                      type="text"
                      className={styles.formControl}
                      required
                      maxLength={200}
                      value={platform}
                      disabled={isLocked}
                      onChange={(e) => setPlatform(e.target.value)}
                      placeholder="e.g. Coursera"
                    />
                  </div>

                  {/* 5. Completion Date */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-completion-date">
                      <span className={styles.required}>*</span> Completion Date
                    </label>
                    <input
                      id="sce-completion-date"
                      type="date"
                      className={styles.formControl}
                      required
                      value={completionDate}
                      disabled={isLocked}
                      onChange={(e) => setCompletionDate(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  {/* 6. Certificate Upload (optional — replaces existing file) */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="sce-upload">
                      Certificate Upload
                    </label>
                    <input
                      id="sce-upload"
                      type="file"
                      className={styles.formControl}
                      accept=".pdf,.jpg,.jpeg,.png"
                      disabled={isLocked}
                      onChange={handleFileChange}
                    />
                    {existingDocId && !selectedFile && (
                      <small style={{ color: "#6c757d", marginTop: 4 }}>
                        📎 A certificate is already on file. Upload a new one to replace it.
                      </small>
                    )}
                    {fileError && (
                      <small style={{ color: "#dc3545", marginTop: 4 }}>{fileError}</small>
                    )}
                    {selectedFile && (
                      <small style={{ color: "#28a745", marginTop: 4 }}>
                        ✅ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB) — ready to upload
                      </small>
                    )}
                  </div>
                </div>

                {/* Submit row */}
                <div className={styles.buttonRow}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={{ marginRight: 16, padding: "12px 32px", borderRadius: 8, fontSize: "1rem" }}
                    onClick={() => navigate("/records")}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.primaryBtn}
                    disabled={submitting || !!fileError || isLocked}
                  >
                    {submitting ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Success Toast */}
      {showToast && (
        <div
          className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
          style={{ zIndex: 9999 }}
        >
          <div className="toast show shadow">
            <div className="toast-header bg-success text-white">
              <strong className="me-auto">✅ Success</strong>
            </div>
            <div className="toast-body text-success fw-semibold text-center">
              Certificate updated successfully!
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCertificateEdit;
